/**
 * The ONE authority on an exercise's BAND: the range a client climbs inside before the column that
 * jumps actually jumps — "8–12 powtórzeń, wtedy +2,5 kg".
 *
 * A band is ONE state read TWO ways, and that is the whole reason it needs an authority. On the wire
 * it is two numbers per set: the banded column's ordinary prescription is the FLOOR, and
 * `SetDraft.prescribedCeiling` is the top. On screen it is a single token — "8–12" under a matrix
 * cell, "12 powt." in the rule's sentence, one field in the sheet. Nothing about the ceiling says
 * which column it belongs to; the exercise's `PreviousExecution` rule does, through its `banded`
 * field. Read the two apart and they drift.
 *
 * THE CEILING CARRIES NO COLUMN OF ITS OWN. That is the invariant every function here protects: 12
 * repetitions and 12 seconds are the same number, so moving the band to another column ORPHANS every
 * ceiling the trainer authored (`clearBand`), and an exercise whose rules band two different columns
 * has a ceiling nobody can read (`bandOf` answers null rather than guessing). One rule may carry a
 * band; a second one asking for its own would need a second ceiling per set, which the wire has no
 * room for — and the backend says the same (`RequireSingleDynamicRule`).
 *
 * Two ROW KINDS never carry one, for the same reason in both cases — the row's prescription is
 * authored once and rides flat, so there is no bar for the client to climb:
 *   · a WARM-UP, which no rule of any type resolves at any moment (plan-progression R6), and
 *   · an AMRAP row, whose rep count is already a FLOOR ("5+") deliberately kept comparable across
 *     cycles. A ceiling on top of it would say "do at least 5, and at most 12" — the opposite
 *     instruction to the one the AMRAP style is written to give.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/progressionBand.ts` ────────────
 * The reading and the write are 1:1, keyed on `localId` where mobile keys on `tempId`. Two of its
 * display helpers did NOT come across: `bandCellText` and `bandCellPatch` are the SET TABLE's range
 * cell, which is its own round (the table has no such cell yet), and porting them now would leave
 * two unimported functions claiming to be the authority on a control that does not exist.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import {
  clampMeasurementValue,
  measurementFieldDefinition,
  type MeasurementFieldKey,
} from '~/shared/measurement';

import type { ExerciseDraft, SetDraft } from './planDraft.types';
import { ruleFor, type ProgressionRules } from './progressionRules';
import { countsAsWork } from './setCounting';
import { measurementValueKey } from './setGridFields';
import { isAmrapSet } from './setShape';

/** What an exercise's ONE banded rule reads as: which column jumps, which the client climbs, how high. */
export interface ProgressionBand {
  /** The column the rule ADVANCES — the key the rule sits under. */
  readonly steppedField: MeasurementFieldKey;
  /** The column the client climbs — the rule's `banded` field, and the one the ceilings are written on. */
  readonly gatedField: MeasurementFieldKey;
  /** Each banded set's authored ceiling, by its `localId`. A set carrying none is simply absent. */
  readonly ceilingBySetLocalId: ReadonlyMap<string, number>;
}

/** The rows a band may sit on — see the module note on warm-ups and AMRAP. */
export function carriesBand(set: SetDraft): boolean {
  // `countsAsWork` rather than a second spelling of the warm-up discriminator: the workspace rule
  // lives in ONE function so the next set type reaches every predicate that depends on it.
  return countsAsWork(set.setType) && !isAmrapSet(set);
}

/**
 * The exercise's ONE BANDED `PreviousExecution` rule, as `[steppedField, gatedField]`, or null when
 * there is not exactly one. Zero means no rule places a bar; two or more mean an authored ceiling
 * names no column, because the number on the set says nothing about which rule it belongs to.
 *
 * A RULE WITH NO BANDED COLUMN IS SKIPPED BEFORE THE COUNT, not counted as a band naming nothing.
 * Such a rule is complete and commonplace — it advances on the delivered set — and pairing it with a
 * null would make two unbanded rules read as "two bands" and null out a third rule's real one.
 */
function bandedRuleOf(rules: ProgressionRules): readonly [MeasurementFieldKey, MeasurementFieldKey] | null {
  const banded = (Object.keys(rules) as MeasurementFieldKey[]).flatMap((field) => {
    const rule = ruleFor(rules, field);
    return rule !== null && rule.type === 'PreviousExecution' && rule.banded !== null
      ? [[field, rule.banded] as const]
      : [];
  });
  return banded.length === 1 ? (banded[0] ?? null) : null;
}

/** The two columns the exercise's ONE execution-gated rule names. */
export interface BandedRuleColumns {
  readonly steppedField: MeasurementFieldKey;
  readonly gatedField: MeasurementFieldKey;
}

/**
 * The columns the band's SHEET is authored over — a different question from `bandOf`, and the
 * distinction is why a sheet used to unmount under the trainer's fingers.
 *
 * `bandOf` answers "is there a bar in the plan", and a rule whose every ceiling has just been erased
 * has none. This answers "is there a rule to author a bar ON", which survives an empty field: the
 * gate lives on the rule and the ceilings are numbers the trainer is in the middle of typing.
 */
export function bandedRuleColumns(rules: ProgressionRules): BandedRuleColumns | null {
  const banded = bandedRuleOf(rules);
  return banded === null ? null : { steppedField: banded[0], gatedField: banded[1] };
}

/**
 * The column the exercise's ONE banded rule GATES on, from a rule map alone — the same question
 * `bandOf` asks, minus the sets. The set table needs it before any ceiling exists on the rows.
 */
export function bandedGateOf(rules: ProgressionRules): MeasurementFieldKey | null {
  return bandedRuleOf(rules)?.[1] ?? null;
}

/**
 * The band this exercise carries, or null when it carries none.
 *
 * NULL IS THE ANSWER FOR AN UNBANDED EXECUTION-GATED RULE TOO, and that is not an oversight. A rule
 * naming no banded column — or naming one and carrying no ceiling on any row — is the default
 * reading of "od wyniku": deliver the whole set and the column jumps (R3), which is a complete,
 * legal configuration rather than a half-authored band.
 */
export function bandOf(
  exercise: Pick<ExerciseDraft, 'progressionRules' | 'sets'>,
): ProgressionBand | null {
  const banded = bandedRuleOf(exercise.progressionRules);
  if (banded === null) return null;

  const ceilingBySetLocalId = new Map<string, number>();
  for (const set of exercise.sets) {
    if (carriesBand(set) && set.prescribedCeiling !== null) {
      ceilingBySetLocalId.set(set.localId, set.prescribedCeiling);
    }
  }
  return ceilingBySetLocalId.size === 0
    ? null
    : { steppedField: banded[0], gatedField: banded[1], ceilingBySetLocalId };
}

/**
 * Write ONE band across the exercise's rows: the CEILING onto every row that carries the band, and
 * the floor only where the row has none of its own.
 *
 * THE CEILING IS THE RULE'S; THE FLOOR IS THE ROW'S. A ceiling names the bar the client has to clear
 * before the stepped column moves, and the rule states it once, so writing it across the rows is
 * what the trainer asked for. A floor is the gated column's ordinary prescription, authored per row
 * — a descending wave of 10 / 8 / 6 is programming, not a copy of whatever number leads the sheet's
 * field. Overwriting it flattens every such exercise to one number the moment a band is touched.
 *
 * So the floor is a SEED, taken only by a row carrying nothing to seed over. `overwriteFloor` is the
 * ONE exception and belongs to the SHEET alone: a trainer who types "8–12" into one field over rows
 * already reading 10 got "10–12" back, half of what they wrote silently discarded on the one screen
 * showing them what they wrote. The pair is one prescription when it is TYPED as one.
 *
 * Both ends go through `clampMeasurementValue`, the same gate the set table's writes converge on: a
 * ceiling outside the column's dictionary range is refused at save with no code to translate it back.
 *
 * Warm-ups and AMRAP rows keep their own floor and are CLEARED of any ceiling. A column the wire
 * holds as text has no number to write, so the sets ride through unchanged rather than half-written.
 */
export function writeBand(
  sets: readonly SetDraft[],
  gatedField: MeasurementFieldKey,
  floor: number | null,
  ceiling: number | null,
  overwriteFloor = false,
): SetDraft[] {
  const valueKey = measurementValueKey(gatedField);
  if (valueKey === null) return [...sets];
  const clampedCeiling = ceiling === null ? null : clampMeasurementValue(gatedField, ceiling);
  return sets.map((set) => {
    if (!carriesBand(set)) return set.prescribedCeiling === null ? set : { ...set, prescribedCeiling: null };
    const banded: SetDraft = { ...set, prescribedCeiling: clampedCeiling };
    if (floor !== null && (overwriteFloor || banded[valueKey] === null)) {
      return { ...banded, [valueKey]: clampMeasurementValue(gatedField, floor) };
    }
    return banded;
  });
}

/**
 * Drop every authored ceiling, leaving the floors exactly where they are — what MOVING THE GATE to
 * another column has to do, and what turning the band off does.
 *
 * The floors stay because they are the gated column's ordinary prescription, which the client is
 * still given; only the bar above it goes. And the ceilings cannot travel with the gate: the number
 * is written in the old column's unit, and 12 repetitions is not 12 seconds.
 */
export function clearBand(sets: readonly SetDraft[]): SetDraft[] {
  return sets.map((set) => (set.prescribedCeiling === null ? set : { ...set, prescribedCeiling: null }));
}

/** What a typed "8" / "8–12" reads as. `invalid` is a refusal to show; `empty` is a blank to fill. */
export type NumberOrRange =
  | { readonly kind: 'empty' }
  | { readonly kind: 'single'; readonly value: number }
  | { readonly kind: 'range'; readonly floor: number; readonly ceiling: number }
  | { readonly kind: 'invalid' };

const EMPTY: NumberOrRange = { kind: 'empty' };
const INVALID: NumberOrRange = { kind: 'invalid' };

/** Every dash a trainer might type or paste, plus the EN DASH the app itself prints. */
const RANGE_SEPARATORS = /[-–—]/;

/**
 * A PLAIN non-negative decimal and nothing else — the only shape this field accepts.
 *
 * `Number()` is far more generous than a prescription is: it reads "1e5" as a hundred thousand,
 * "0x10" as sixteen, "Infinity" as infinity and " 8 " as eight. None of those is a thing a trainer
 * types into a repetitions field, and each of them reached the rows as a prescription nobody meant.
 */
const PLAIN_DECIMAL = /^\d+([.,]\d+)?$/;

/**
 * One typed number in the column's own unit, or null when it is not one the column can take. The
 * DICTIONARY decides the bounds — the same range the set table is held to and the same one the
 * backend's band validation refuses against, with no machine code to translate the refusal back.
 */
function parseOne(raw: string, field: MeasurementFieldKey | null): number | null {
  const trimmed = raw.trim();
  if (!PLAIN_DECIMAL.test(trimmed)) return null;
  const value = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(value)) return null;
  if (field === null) return value;
  const { min, max } = measurementFieldDefinition(field);
  if (min !== null && value < min) return null;
  if (max !== null && value > max) return null;
  return value;
}

/**
 * Read the band sheet's ONE control: a single number is a plain prescription, two are a band. The
 * second number is OPTIONAL rather than a mode — that is the whole point of one control instead of
 * two — so "8" and "8–12" come out of the same field and the same parse.
 *
 * A range whose floor is not BELOW its ceiling is refused rather than normalised. "12–8" is a slip
 * and "8–8" is a band with nothing to climb; silently swapping or collapsing them would commit a
 * prescription the trainer did not write, on the one screen where they are looking at what they typed.
 */
export function parseNumberOrRange(
  raw: string,
  field: MeasurementFieldKey | null = null,
): NumberOrRange {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return EMPTY;

  // Skip a leading sign so "-5" reads as one (refused) number rather than an empty-to-5 range.
  const separatorAt = trimmed.slice(1).search(RANGE_SEPARATORS);
  if (separatorAt === -1) {
    const value = parseOne(trimmed, field);
    return value === null ? INVALID : { kind: 'single', value };
  }

  const floor = parseOne(trimmed.slice(0, separatorAt + 1), field);
  const ceiling = parseOne(trimmed.slice(separatorAt + 2), field);
  if (floor === null || ceiling === null || floor >= ceiling) return INVALID;
  return { kind: 'range', floor, ceiling };
}

/**
 * A floor and the ceiling over it, in the notation the band FIELD holds — "8–12", "8", or blank.
 *
 * Raw rather than display notation, because this text goes into a control the trainer edits and
 * `parseNumberOrRange` reads straight back. A ceiling at or under its floor is not a band, so it
 * prints as the single number it is.
 */
export function bandText(floor: number | null, ceiling: number | null): string {
  if (floor === null) return '';
  return ceiling !== null && ceiling > floor ? `${floor}–${ceiling}` : String(floor);
}

/**
 * The exercise's band as the ONE field in the sheet shows it: "8–12", "8", or blank.
 *
 * IT READS THE ROWS THAT CARRY THE BAND AND NOTHING ELSE. Seeding from the first set holding any
 * number at all lands on the WARM-UP on an exercise that has one — a row no rule resolves and that
 * therefore never carries a ceiling — so the field opens on the warm-up's bare "10" while the
 * working rows say "8–12", and committing that back flattens the band to a number nobody typed.
 */
export function bandFieldText(
  exercise: Pick<ExerciseDraft, 'sets'>,
  gatedField: MeasurementFieldKey,
): string {
  const valueKey = measurementValueKey(gatedField);
  if (valueKey === null) return '';
  for (const set of exercise.sets) {
    if (!carriesBand(set)) continue;
    const floor = set[valueKey];
    if (floor === null) continue;
    return bandText(floor, set.prescribedCeiling);
  }
  return '';
}

/**
 * What the band field OPENS ON when the trainer turns the range on: the gated column's own
 * prescription and one unit above it, "8–9" over rows prescribing 8.
 *
 * IT SEEDS ONLY FROM A PAIR THE ROWS ALREADY CARRY. A column priced with a floor and a ceiling opens
 * on „8–12"; anything else opens BLANK. An earlier revision seeded a ceiling one unit above a lone
 * floor, on the argument that a blank field is a question with no answer visible — and that ceiling
 * did not stay on screen: it travelled to the wire on the next commit, so a column prescribing 8
 * opened as „8–9" and the client was judged against a 9 nobody typed. A blank field asks; an
 * invented pair answers wrongly.
 *
 * The same restraint covers a column no working row prices at all: no floor, nothing to seed from,
 * and a floor picked out of the air would land across every row on the next blur.
 */
export function bandPrefillText(
  exercise: Pick<ExerciseDraft, 'sets'>,
  gatedField: MeasurementFieldKey,
): string {
  const seed = bandFieldText(exercise, gatedField);
  // A row already carrying a PAIR keeps it. A lone floor does not become one: a ceiling seeded one
  // unit above it is a bar nobody typed, and it does not stay on screen — it travels to the wire on
  // the next commit, so „8" opened as „8–9" and the client was judged against a 9.
  return parseNumberOrRange(seed, gatedField).kind === 'range' ? seed : '';
}
